import type { Dispatch, RefObject } from 'react';

import { focusElementWithRetry } from '../../../../forms/date-time-picker/utils/focusDateTimeInput.js';
import type { DateTimeInputRef } from '../../../../forms/date-time-range-picker/types.js';
import type { FormFieldMessagesReducerAction } from '../../../../forms/forms-core/state/formfield-messages-state-reducer.js';
import {
  getTimeValueFromSimpleValue,
  sortTimeframeValues,
} from '../../../../forms/forms-core/time/helpers.js';
import { type Timeframe } from '../../../types/time.js';
import { isFullTimeframe, isNowTimeValue } from '../../helpers.js';
import { validate, isOutOfBounds } from '../../validation.js';
import type { ValidateOptions } from '../../validation.js';
import type { DateTimeRangePickerTimeframeStates } from '../dateTimeRangePicker.types.js';

/**
 * Closes Calendar overlay and, after two animation frames, refocuses the from-input.
 * The double rAF ensures the overlay has fully unmounted before focus is moved.
 * @internal
 */
export function closeCalendarAndFocusInput(
  pickerRef:
    | RefObject<{ close(): void; focus?: () => void } | null>
    | undefined,
  dateTimeFromRef: RefObject<DateTimeInputRef | null> | undefined,
): void {
  pickerRef?.current?.close();
  focusElementWithRetry(
    () =>
      dateTimeFromRef?.current?.spinButtonHandlers.get('hour')?.element.current,
    dateTimeFromRef,
  );
}

/**
 * Pure reducer for the `clearCalendarValue` action, shared between
 * DateTimeRangePicker and TimeframeSelector.
 * @internal
 */
export function handleClearCalendarValue(
  state: Pick<
    DateTimeRangePickerTimeframeStates,
    'calendarValue' | 'dateTimeRangePickerTimeframe' | 'nowInserted'
  >,
): Partial<
  Pick<
    DateTimeRangePickerTimeframeStates,
    'calendarValue' | 'dateTimeRangePickerTimeframe' | 'nowInserted'
  >
> {
  // Guard: complete range or no selection — nothing to revert.
  if (
    !state.calendarValue ||
    (state.calendarValue.from && state.calendarValue.to)
  ) {
    return {};
  }

  // Pending "now" boundary set via the now-button: `to = now` with no `from` yet.
  // Only revert when the value originated from the button — user-typed "now" must be preserved.
  if (
    state.nowInserted &&
    isNowTimeValue(state.dateTimeRangePickerTimeframe?.to) &&
    !state.dateTimeRangePickerTimeframe?.from
  ) {
    return {
      calendarValue: null,
      dateTimeRangePickerTimeframe: null,
      nowInserted: false,
    };
  }

  // Standard restore-on-close: restore calendar value from stored timeframe.
  if (
    state.dateTimeRangePickerTimeframe?.from ||
    state.dateTimeRangePickerTimeframe?.to
  ) {
    return {
      calendarValue: state.dateTimeRangePickerTimeframe,
    };
  }

  return { calendarValue: null };
}

/**
 * Options for {@link applySetToNow}.
 * @internal
 */
export type SetToNowOptions = {
  /** Which boundary to set to `now`. Defaults to `to`. */
  target?: 'from' | 'to';
  /** Called with the committed timeframe when a full range is built. */
  onChangeProp?: (value: Timeframe | null) => void;
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>;
  /** Controls validation messaging and whether to swap an inverted boundary. */
  validateMode: 'dateTimePicker' | 'timeframeSelector';
  errorMessages?: ValidateOptions['errorMessages'];
  /** When true the component is controlled: skip internal state update after firing `onChangeProp`. */
  isControlled?: boolean;
};

/**
 * Returns the store state patch for the "set a boundary to now" action, shared by
 * DateTimeRangePicker and TimeframeSelector. Callers may spread in additional
 * component-specific fields on top of the returned patch.
 * @internal
 */
export function applySetToNow(
  state: DateTimeRangePickerTimeframeStates,
  options: SetToNowOptions,
): Partial<DateTimeRangePickerTimeframeStates> {
  // When min is a "now" expression the component operates in a "starts from now"
  // mode — the button always anchors the "from" boundary to now.
  const defaultTarget = isNowTimeValue(state.min) ? 'from' : 'to';
  const { target = defaultTarget } = options;

  // If "now" is outside the allowed min, max range the whole action is a no-op.
  // The "Set time to now" button is already visually disabled in this case.
  const nowIsOutOfBounds =
    isOutOfBounds('now', state.min, state.max) !== undefined;

  if (nowIsOutOfBounds) {
    return state;
  }

  const nowExpression = getTimeValueFromSimpleValue(
    'now',
    state.timeOffset,
    target,
    true,
    state.min,
    state.max,
    state.precision,
  );

  const activeRangeValue =
    state.calendarValue ?? state.dateTimeRangePickerTimeframe;
  const fromValue = activeRangeValue?.from ?? null;
  const toValue = activeRangeValue?.to ?? null;
  const existingTimeValue =
    target === 'from' ? toValue ?? fromValue : fromValue ?? toValue;

  // Treat an out-of-bounds preserved boundary as absent — clear it and keep the calendar open.
  const existingTimeValueOutOfBounds =
    existingTimeValue &&
    isOutOfBounds(existingTimeValue?.value, state.min, state.max) !== undefined;

  if (!existingTimeValue || existingTimeValueOutOfBounds) {
    if (target === 'from') {
      // No valid complementary boundary yet — set from=now and let the user pick to next.
      return {
        dateTimeRangePickerTimeframe: { from: nowExpression, to: null },
        calendarValue: { from: nowExpression, to: null },
      };
    }
    return {
      dateTimeRangePickerTimeframe: { from: null, to: nowExpression },
      calendarValue: { from: null, to: nowExpression },
    };
  }

  const timeframe = sortTimeframeValues(
    { from: existingTimeValue, to: nowExpression },
    state.min,
    state.max,
    state.precision,
  );

  if (isFullTimeframe(timeframe)) {
    options.onChangeProp?.(timeframe);
    closeCalendarAndFocusInput(
      state.dateTimeRangePickerRef,
      state.dateTimeFromRef,
    );

    if (options.isControlled) {
      return state;
    }
  }

  const error = validate(timeframe, state.min, state.max, {
    formMessageDispatch: options.formFieldMessagesDispatch,
    formerError: state.dateTimeRangePickerError,
    precision: state.precision,
    showError: false,
    checkOrder: true,
    ref: state.dateTimeRangePickerRef,
    expectedFormat: state.formatString,
    valueMode: options.validateMode,
    required: state.required,
    errorMessages: options.errorMessages,
  });

  return {
    dateTimeRangePickerTimeframe: timeframe,
    calendarValue: timeframe,
    calendarError: error,
    dateTimeRangePickerError: error,
  };
}
